---
title: Cart to checkout
description: Build a cart, apply discounts, collect the address and shipping, take payment and show the order.
order: 5
---

This recipe walks one guest order from an empty cart to the order status page. Signed-in shoppers
follow the same steps with their customer token; the differences are in
[Customer accounts](/docs/guides/customer-accounts#carts-for-signed-in-shoppers). The examples
reuse `NUCLEUS_API_URL`, `NUCLEUS_KEY` and the SDK client `storefront` from
[Getting started](/docs); the curl examples read the tokens and ids earlier steps returned from
shell variables.

<Callout type="warning" title="These calls create real data">
  Carts, checkouts and orders created with your key are real records in your store. A manual payment
  method places a real unpaid order that shows up in the admin.
</Callout>

## Build the cart

<Steps>

### Create a cart

<EndpointLink id="createCart" /> starts a cart, optionally with lines and the `country` that picks
its market. Keep the returned `token`: it is the only handle on the cart, so store it where the
shopper's browser keeps it (a cookie or local storage) until `expiresAt`.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/carts" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{
    \"country\": \"US\",
    \"lines\": [{
      \"variantId\": \"$PENDANT_VARIANT_ID\",
      \"quantity\": 1,
      \"customizations\": [{ \"key\": \"engraving\", \"value\": \"A & R\" }]
    }]
  }"
```

```ts title="fetch"
const pendantPage = await fetch(`${NUCLEUS_API_URL}/storefront/v1/products/drift-diamond-pendant`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
}).then((response) => response.json());

const cart = await fetch(`${NUCLEUS_API_URL}/storefront/v1/carts`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    country: 'US',
    lines: [
      {
        variantId: pendantPage.variants[0].id,
        quantity: 1,
        customizations: [{ key: 'engraving', value: 'A & R' }],
      },
    ],
  }),
}).then((response) => response.json());
```

```ts title="SDK"
const pendantPage = await storefront.getProduct({ handle: 'drift-diamond-pendant' });

const cart = await storefront.createCart({
  country: 'US',
  lines: [
    {
      variantId: pendantPage.variants[0]!.id,
      quantity: 1,
      customizations: [{ key: 'engraving', value: 'A & R' }],
    },
  ],
});
// cart.token, cart.lines, cart.subtotal, cart.total
```

</CodeTabs>

### Add lines

<EndpointLink id="addCartLine" /> adds a variant with its customizations and answers the whole cart.
Adding the same variant with the same customizations again raises that line's quantity instead of
adding a second line. Customizations are checked against the product: a missing required one, such
as a ring size, fails with `422 INVALID_INPUT` and `meta.missing`.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/carts/$CART_TOKEN/lines" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{
    \"variantId\": \"$BAND_VARIANT_ID\",
    \"quantity\": 1,
    \"customizations\": [{ \"key\": \"ring_size\", \"value\": \"6\" }]
  }"
```

```ts title="fetch"
const bandPage = await fetch(`${NUCLEUS_API_URL}/storefront/v1/products/orbit-comfort-fit-band`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
}).then((response) => response.json());

const withBand = await fetch(`${NUCLEUS_API_URL}/storefront/v1/carts/${cart.token}/lines`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    variantId: bandPage.variants[0].id,
    quantity: 1,
    customizations: [{ key: 'ring_size', value: '6' }],
  }),
}).then((response) => response.json());
```

```ts title="SDK"
const bandPage = await storefront.getProduct({ handle: 'orbit-comfort-fit-band' });

const withBand = await storefront.addCartLine(
  { token: cart.token },
  {
    variantId: bandPage.variants[0]!.id,
    quantity: 1,
    customizations: [{ key: 'ring_size', value: '6' }],
  },
);
```

</CodeTabs>

### Change quantities

<EndpointLink id="updateCartLine" /> sets a line's quantity; `0` removes it, as does
<EndpointLink id="removeCartLine" />. Email, locale, a `country` for another market and free-form
`attributes` (a gift-wrap flag, a delivery note) go on the cart itself with
<EndpointLink id="updateCart" />.

<CodeTabs>

```bash title="curl"
curl -X PATCH "$NUCLEUS_API_URL/storefront/v1/carts/$CART_TOKEN/lines/$LINE_ID" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "quantity": 2 }'
```

```ts title="fetch"
const pendantLine = withBand.lines[0];
const twoPendants = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/carts/${cart.token}/lines/${pendantLine.id}`,
  {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ quantity: 2 }),
  },
).then((response) => response.json());
```

```ts title="SDK"
const pendantLine = withBand.lines[0]!;
const twoPendants = await storefront.updateCartLine(
  { token: cart.token, lineId: pendantLine.id },
  { quantity: 2 },
);
```

</CodeTabs>

### Apply a discount code

<EndpointLink id="applyCartDiscount" /> adds a code. The cart then lists it in `discountCodes`,
shows what it took off in `discountApplications` and `discount`, and prices `total` with it. A code
that does not apply fails with `422 DISCOUNT_NOT_APPLICABLE` and a machine-readable `meta.reason`
(for example `unknown`) you can turn into a message.
<EndpointLink id="removeCartDiscount" /> takes it off again.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/carts/$CART_TOKEN/discounts" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "code": "WELCOME10" }'
```

```ts title="fetch"
const discounted = await fetch(`${NUCLEUS_API_URL}/storefront/v1/carts/${cart.token}/discounts`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ code: 'WELCOME10' }),
}).then((response) => response.json());
```

```ts title="SDK"
const discounted = await storefront.applyCartDiscount({ token: cart.token }, { code: 'WELCOME10' });
// discounted.discountApplications -> [{ code: 'WELCOME10', amount: { amount: 24000, currency: 'USD' } }]
```

</CodeTabs>

</Steps>

## Check out

<Steps>

### Start the checkout

<EndpointLink id="createCheckout" /> turns the cart into a checkout and answers with its own
`token`. Calling it again for the same cart returns the open checkout instead of a second one, so a
"Checkout" button can call it every time.

The checkout says what is still missing in `blockers`, and `readyForPayment` turns `true` once the
list is empty:

| Blocker             | Resolve it by                                               |
| ------------------- | ----------------------------------------------------------- |
| `email`             | Setting `email`                                             |
| `shipping_address`  | Setting `shippingAddress`                                   |
| `shipping_rate`     | Choosing one of `availableShippingRates`                    |
| `no_shipping_rate`  | Nothing ships to this address: ask for another one          |
| `unavailable_lines` | Removing the lines that no longer sell (`available: false`) |
| `empty`             | Adding a line to the cart                                   |

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/checkouts" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{ \"cartToken\": \"$CART_TOKEN\" }"
```

```ts title="fetch"
const checkout = await fetch(`${NUCLEUS_API_URL}/storefront/v1/checkouts`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ cartToken: cart.token }),
}).then((response) => response.json());
// checkout.blockers -> ['email', 'shipping_address']
```

```ts title="SDK"
const checkout = await storefront.createCheckout({ cartToken: cart.token });
// checkout.blockers -> ['email', 'shipping_address']
```

</CodeTabs>

### Add contact details and the address

<EndpointLink id="updateCheckout" /> takes the contact details and addresses. With a shipping
address in place the checkout lists `availableShippingRates` with their prices; pick one with a
second call. Changing the address later clears the chosen rate, because the rates may differ.
`billingAddress: null` means "same as shipping".

<CodeTabs>

```bash title="curl"
curl -X PATCH "$NUCLEUS_API_URL/storefront/v1/checkouts/$CHECKOUT_TOKEN" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "email": "asha@example.com",
    "shippingAddress": {
      "firstName": "Asha", "lastName": "Mehta", "line1": "350 Fifth Avenue",
      "city": "New York", "region": "NY", "postalCode": "10118", "countryCode": "US",
      "phone": "+12125550100"
    }
  }'

# Then choose a rate from availableShippingRates:
curl -X PATCH "$NUCLEUS_API_URL/storefront/v1/checkouts/$CHECKOUT_TOKEN" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{ \"shippingRateId\": \"$SHIPPING_RATE_ID\" }"
```

```ts title="fetch"
const addressed = await fetch(`${NUCLEUS_API_URL}/storefront/v1/checkouts/${checkout.token}`, {
  method: 'PATCH',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'asha@example.com',
    shippingAddress: {
      firstName: 'Asha',
      lastName: 'Mehta',
      line1: '350 Fifth Avenue',
      city: 'New York',
      region: 'NY',
      postalCode: '10118',
      countryCode: 'US',
      phone: '+12125550100',
    },
  }),
}).then((response) => response.json());

const ready = await fetch(`${NUCLEUS_API_URL}/storefront/v1/checkouts/${checkout.token}`, {
  method: 'PATCH',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ shippingRateId: addressed.availableShippingRates[0].id }),
}).then((response) => response.json());
// ready.readyForPayment -> true
```

```ts title="SDK"
const addressed = await storefront.updateCheckout(
  { token: checkout.token },
  {
    email: 'asha@example.com',
    shippingAddress: {
      firstName: 'Asha',
      lastName: 'Mehta',
      line1: '350 Fifth Avenue',
      city: 'New York',
      region: 'NY',
      postalCode: '10118',
      countryCode: 'US',
      phone: '+12125550100',
    },
  },
);

const ready = await storefront.updateCheckout(
  { token: checkout.token },
  { shippingRateId: addressed.availableShippingRates[0]!.id },
);
// ready.readyForPayment -> true
```

</CodeTabs>

### List the payment methods

<EndpointLink id="listCheckoutPaymentMethods" /> answers what the shopper can pay with: a `stripe`
entry when the store has Stripe connected (with the wallets it offers and the Stripe
`publishableKey` for Stripe.js), one `manual` entry per active manual method such as bank transfer
or cash on delivery, with its `name`, `fee` and whether it is `available` for this checkout's
amount. Once gift cards are applied, a `gift_card` entry says how much of the total they cover.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/checkouts/$CHECKOUT_TOKEN/payment-methods" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const methods = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/checkouts/${checkout.token}/payment-methods`,
  { headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());
```

```ts title="SDK"
const methods = await storefront.listCheckoutPaymentMethods({ token: checkout.token });
// methods.items -> [{ type: 'stripe', wallets, publishableKey },
//                   { type: 'manual', id, key: 'bank_transfer', name, fee, available }, ...]
```

</CodeTabs>

### Apply a gift card (optional)

<EndpointLink id="applyCheckoutGiftCard" /> redeems a gift card code against the checkout. The
checkout lists it in `giftCardApplications` and lowers `amountDue`, the part left to pay; `total`
stays the order total. <EndpointLink id="removeCheckoutGiftCard" /> takes it off by its last four
characters.

</Steps>

## Take payment

<EndpointLink id="completeCheckout" /> places the order. Its body names how the shopper pays, and
the three kinds behave differently.

### Stripe

#### Create the PaymentIntent

<EndpointLink id="createPaymentIntent" /> creates, or refreshes after a change, the Stripe
PaymentIntent for `amountDue`. It answers the `clientSecret` and the store's Stripe `publishableKey`
for Stripe.js. It fails with `409 PAYMENT_PROVIDER_UNAVAILABLE` when the store has no connected
Stripe account, and with `409` while the checkout still has blockers.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/checkouts/$CHECKOUT_TOKEN/payment-intent" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const intent = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/checkouts/${checkout.token}/payment-intent`,
  { method: 'POST', headers: { Authorization: `Bearer ${NUCLEUS_KEY}` } },
).then((response) => response.json());
```

```ts title="SDK"
const intent = await storefront.createPaymentIntent({ token: checkout.token });
// { paymentIntentId, clientSecret, publishableKey, amount, captureMethod, status }
```

</CodeTabs>

#### Confirm it, then place the order

Mount Stripe's Payment Element with the `clientSecret` and confirm it in the browser. Once Stripe
reports success, call <EndpointLink id="completeCheckout" /> with `type: "stripe"`.

```ts title="Stripe.js"
import { loadStripe } from '@stripe/stripe-js';

const stripe = await loadStripe(intent.publishableKey);
const elements = stripe!.elements({ clientSecret: intent.clientSecret });
elements.create('payment').mount('#payment-element');

// On submit:
const result = await stripe!.confirmPayment({
  elements,
  redirect: 'if_required',
  confirmParams: { return_url: `${location.origin}/checkout/return` },
});
if (!result.error) {
  const paidOrder = await storefront.completeCheckout(
    { token: checkout.token },
    { type: 'stripe', paymentIntentId: intent.paymentIntentId },
  );
}
```

While the payment is still processing, or when it failed, the call answers
`409 PAYMENT_NOT_COMPLETED` with the Stripe status in `meta.status`. For `processing`, show a
"payment pending" page: Stripe's webhook places the order when the payment succeeds.

### Manual methods

A manual method, such as bank transfer or cash on delivery, places the order at once as `unpaid`
and returns the method's `paymentInstructions` to show the shopper. The merchant marks the order
paid in the admin when the money arrives.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/checkouts/$CHECKOUT_TOKEN/payment" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{ \"type\": \"manual\", \"manualPaymentMethodId\": \"$PAYMENT_METHOD_ID\" }"
```

```ts title="fetch"
const bankTransfer = methods.items.find(
  (method: { type: string; key?: string }) =>
    method.type === 'manual' && method.key === 'bank_transfer',
);
const order = await fetch(`${NUCLEUS_API_URL}/storefront/v1/checkouts/${checkout.token}/payment`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ type: 'manual', manualPaymentMethodId: bankTransfer.id }),
}).then((response) => response.json());
```

```ts title="SDK"
const bankTransfer = methods.items.find(
  (method) => method.type === 'manual' && method.key === 'bank_transfer',
);
if (bankTransfer?.type !== 'manual') throw new Error('The store offers no bank transfer');

const order = await storefront.completeCheckout(
  { token: checkout.token },
  { type: 'manual', manualPaymentMethodId: bankTransfer.id },
);
// order.name -> '#1003', order.financialStatus -> 'unpaid', order.paymentInstructions
```

</CodeTabs>

### Gift cards only

When applied gift cards cover the whole total (`amountDue` is zero), send `{ "type": "gift_card" }`.
With a manual method or Stripe, applied gift cards are redeemed first and the method pays the rest.

## After the order

The response is the placed order: `name` (the number the shopper sees, such as `#1003`), `lines`,
the totals, `financialStatus` and `statusUrl`. The cart is used up and answers `404` from now on;
start a new one for the next purchase. Calling <EndpointLink id="completeCheckout" /> again for the
same checkout answers the same order, so a retry after a network error does not place it twice.

### The order status page

`statusUrl` is the signed link to the order's status page on your storefront, the same link the
confirmation email carries. It needs the store's primary domain; without one it is `null`. The
last path segment is a token your status page passes to <EndpointLink id="getOrderStatus" />. It
needs no customer token, works for a year, and shows the order as the shopper sees it: status,
lines, fulfillments with tracking, and payment instructions.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/orders/status/$STATUS_TOKEN" \
  -H "Authorization: Bearer $NUCLEUS_KEY"
```

```ts title="fetch"
const statusToken = order.statusUrl.split('/').pop();
const status = await fetch(`${NUCLEUS_API_URL}/storefront/v1/orders/status/${statusToken}`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
}).then((response) => response.json());
```

```ts title="SDK"
const statusToken = order.statusUrl!.split('/').pop()!;
const status = await storefront.getOrderStatus({ token: statusToken });
// status.financialStatus, status.fulfillmentStatus, status.fulfillments
```

</CodeTabs>

### Related flows

- **Abandoned checkouts.** The recovery email links to your storefront with a recovery token;
  <EndpointLink id="recoverCheckout" /> reopens that checkout and extends its expiry.
- **Design approval.** Bespoke pieces wait for the shopper to approve a rendering. The email
  links to your storefront with the order id and a signed token. Send the shopper's answer,
  `{ "decision": "approve" }` or a change request with a message, to
  <EndpointLink id="submitDesignApproval" />.
